/**
 * useAgentChatHistory Hook
 * Manages agent chat history data and operations with paginated loading
 */

import { useCallback, useEffect, useState, useRef } from 'react'
import { chatService, type ChatQueryParams } from '../services/chat-service'
import { useChatEvents } from './use-chat-events'
import { getAuthHeader } from '../utils/auth-storage'

/** Number of chats to load per page */
const PAGE_SIZE = 10

/**
 * Chat history item interface
 */
export interface ChatHistoryItem {
  id: string
  title: string
  createdAt: string
  updatedAt: string
  messageCount?: number
  lastMessage?: any
  metadata?: Record<string, unknown>
}

/**
 * Options for fetching chat history.
 */
interface FetchChatHistoryOptions {
  /** When true, updates in background without toggling loading state. */
  silent?: boolean
}

/**
 * Hook state interface
 */
interface UseAgentChatHistoryState {
  chatHistory: ChatHistoryItem[]
  isLoading: boolean
  error: string | null
  lastFetch: number | null
  hasMore: boolean
  total: number
  isLoadingMore: boolean
  groupedData: Record<string, any[]>
  currentPage: number
}

/**
 * Merges new grouped chat data into existing grouped data, avoiding duplicates.
 */
function mergeGroupedData(
  existing: Record<string, any[]>,
  incoming: Record<string, any[]>
): Record<string, any[]> {
  const merged = { ...existing }
  const groups = ['today', 'yesterday', 'lastWeek', 'lastMonth', 'older']

  for (const group of groups) {
    const existingChats = merged[group] || []
    const incomingChats = incoming[group] || []
    if (incomingChats.length === 0) continue

    const existingIds = new Set(existingChats.map((c: any) => c.id))
    const newChats = incomingChats.filter((c: any) => !existingIds.has(c.id))
    merged[group] = [...existingChats, ...newChats]
  }

  return merged
}

/**
 * Flattens grouped data into a flat array of chat history items.
 */
function flattenGroupedData(groupedData: Record<string, any[]>): ChatHistoryItem[] {
  const allChats: any[] = []
  if (groupedData.today) allChats.push(...groupedData.today)
  if (groupedData.yesterday) allChats.push(...groupedData.yesterday)
  if (groupedData.lastWeek) allChats.push(...groupedData.lastWeek)
  if (groupedData.lastMonth) allChats.push(...groupedData.lastMonth)
  if (groupedData.older) allChats.push(...groupedData.older)

  return allChats.map((chat: any) => ({
    id: chat.id,
    title: chat.title || 'Untitled Chat',
    createdAt: chat.createdAt,
    updatedAt: chat.updatedAt,
    messageCount: chat.messageCount,
    lastMessage: chat.lastMessage,
    metadata: chat.metadata,
  }))
}

/**
 * Custom hook for agent chat history management
 * Provides chat history fetching with load-more pagination, deleting, and state management
 *
 * @param autoFetch - Whether to automatically fetch data on mount
 * @param agentId - Agent ID to fetch chat history for
 * @returns Hook return object with state and methods
 */
export function useAgentChatHistory(
  autoFetch: boolean = false,
  agentId?: string
) {
  const [state, setState] = useState<UseAgentChatHistoryState>({
    chatHistory: [],
    isLoading: false,
    error: null,
    lastFetch: null,
    hasMore: false,
    total: 0,
    isLoadingMore: false,
    groupedData: {},
    currentPage: 1
  })

  const chatEvents = useChatEvents()
  const initialLoadDone = useRef(false)
  const currentAgentId = useRef<string | null>(null)

  /**
   * Clears the current error state
   */
  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }))
  }, [])

  /**
   * Fetches chat history for a specific agent.
   * Resets to page 1 and replaces existing data.
   */
  const fetchChatHistory = useCallback(async (
    targetAgentId: string,
    params?: ChatQueryParams,
    options?: FetchChatHistoryOptions
  ) => {
    try {
      if (!options?.silent) {
        setState(prev => ({ ...prev, isLoading: true, error: null }))
      } else {
        setState(prev => ({ ...prev, error: null }))
      }

      const fetchParams: ChatQueryParams = {
        page: 1,
        limit: PAGE_SIZE,
        ...params,
      }
      const response = await chatService.getAgentChatHistory(targetAgentId, fetchParams)

      if (response.success && response.data) {
        const groupedData = response.data as Record<string, any[]>
        const transformedHistory = flattenGroupedData(groupedData)

        setState(prev => ({
          ...prev,
          chatHistory: transformedHistory,
          groupedData,
          isLoading: false,
          lastFetch: Date.now(),
          error: null,
          hasMore: response.pagination?.hasNextPage || false,
          total: response.pagination?.total || transformedHistory.length,
          currentPage: 1
        }))

        initialLoadDone.current = true
        currentAgentId.current = targetAgentId
      } else {
        throw new Error(response.message || 'Failed to fetch chat history')
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch chat history'

      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage
      }))
    }
  }, [])

  /**
   * Deletes a chat from history
   */
  const deleteChatFromHistory = useCallback(async (chatId: string) => {
    try {
      await chatService.deleteChat(chatId)

      chatEvents.emit("delete-chat", chatId)

      setState(prev => ({
        ...prev,
        chatHistory: prev.chatHistory.filter(chat => chat.id !== chatId),
        total: Math.max(0, prev.total - 1)
      }))
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete chat'
      setState(prev => ({ ...prev, error: errorMessage }))
      throw error
    }
  }, [chatEvents])

  /**
   * Refreshes the chat history (resets to page 1)
   */
  const refresh = useCallback(async (targetAgentId: string, options?: FetchChatHistoryOptions) => {
    await fetchChatHistory(targetAgentId, undefined, options)
  }, [fetchChatHistory])

  /**
   * Loads the next page of chat history and merges into existing grouped data.
   */
  const loadMore = useCallback(async () => {
    if (!currentAgentId.current || state.isLoadingMore || !state.hasMore) return

    const nextPage = state.currentPage + 1

    setState(prev => ({ ...prev, isLoadingMore: true }))

    try {
      const response = await chatService.getAgentChatHistory(currentAgentId.current, {
        page: nextPage,
        limit: PAGE_SIZE,
      })

      if (response.success && response.data) {
        const incomingGrouped = response.data as Record<string, any[]>

        setState(prev => {
          const mergedGrouped = mergeGroupedData(prev.groupedData, incomingGrouped)
          const mergedFlat = flattenGroupedData(mergedGrouped)

          return {
            ...prev,
            chatHistory: mergedFlat,
            groupedData: mergedGrouped,
            isLoadingMore: false,
            hasMore: response.pagination?.hasNextPage || false,
            total: response.pagination?.total || mergedFlat.length,
            currentPage: nextPage,
          }
        })
      } else {
        setState(prev => ({ ...prev, isLoadingMore: false }))
      }
    } catch {
      setState(prev => ({ ...prev, isLoadingMore: false }))
    }
  }, [state.currentPage, state.isLoadingMore, state.hasMore])

  // Auto-fetch on mount if enabled
  useEffect(() => {
    if (autoFetch && agentId) {
      fetchChatHistory(agentId)
    }
  }, [autoFetch, agentId, fetchChatHistory])

  // Listen for chat events and refresh history
  useEffect(() => {
    const unsubscribeNewChat = chatEvents.subscribe("new-chat", (_chatId) => {
      if (initialLoadDone.current && currentAgentId.current) {
        fetchChatHistory(currentAgentId.current, undefined, { silent: true })
      }
    })

    const unsubscribeUpdateChat = chatEvents.subscribe("update-chat", (_chatId) => {
      if (initialLoadDone.current && currentAgentId.current) {
        fetchChatHistory(currentAgentId.current, undefined, { silent: true })
      }
    })

    const unsubscribeDeleteChat = chatEvents.subscribe("delete-chat", (_chatId) => {
      if (initialLoadDone.current && currentAgentId.current) {
        fetchChatHistory(currentAgentId.current, undefined, { silent: true })
      }
    })

    return () => {
      unsubscribeNewChat()
      unsubscribeUpdateChat()
      unsubscribeDeleteChat()
    }
  }, [chatEvents, fetchChatHistory])

  /**
   * Subscribe to trigger lifecycle SSE events from the server.
   * Replaces the 10-second polling interval with real-time push updates.
   * When a trigger stream starts or completes, refreshes the sidebar immediately.
   */
  useEffect(() => {
    if (!initialLoadDone.current || !currentAgentId.current) return

    const serverUrl = import.meta.env.VITE_API_BASE_URL || ''
    const cleanUrl = serverUrl.replace(/\/api$/, '')
    const sseUrl = `${cleanUrl}/api/chat/trigger-events`

    let cancelled = false
    let abortCtrl: AbortController

    const connect = async () => {
      if (cancelled) return
      abortCtrl = new AbortController()

      try {
        const authHeader = getAuthHeader()
        const response = await fetch(sseUrl, {
          headers: { Authorization: authHeader || '', Accept: 'text/event-stream' },
          signal: abortCtrl.signal,
        })
        if (!response.ok || !response.body) return

        const reader = response.body.pipeThrough(new TextDecoderStream()).getReader()
        let buf = ''

        while (!cancelled) {
          const { done, value } = await reader.read()
          if (done) break
          buf += value
          const lines = buf.split('\n')
          buf = lines.pop() || ''
          for (const line of lines) {
            if (!line.startsWith('data: ')) continue
            try {
              const evt = JSON.parse(line.slice(6))
              const agentId = currentAgentId.current
              // Only process events for the current agent
              if (agentId && evt.agentId && evt.agentId !== agentId) continue

              if (evt.type === 'stream-registered' || evt.type === 'stream-completed') {
                if (currentAgentId.current) {
                  fetchChatHistory(currentAgentId.current, undefined, { silent: true })
                }
              }
            } catch { /* ignore parse errors */ }
          }
        }
      } catch {
        // Reconnect after 5s on error (unless cancelled)
        if (!cancelled) setTimeout(connect, 5000)
      }
    }

    connect()
    return () => {
      cancelled = true
      abortCtrl?.abort()
    }
  }, [fetchChatHistory])

  return {
    ...state,
    fetchChatHistory,
    deleteChatFromHistory,
    clearError,
    refresh,
    loadMore,
  }
}
